import { Alert, App, Table, Button, Input, Space, Col, Row, Tooltip, Modal, Form, Spin, Empty, InputRef, Divider, Typography } from 'antd';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  OrganizationCreationStatus,
  OrganizationItem,
  OrganizationItemStore,
  OrganizationMemberItem,
  ORGANIZATIONS_MEMBER_LIMIT,
} from 'src/stores/organizations-store';
import { AppConfig, container } from 'src/inversify.config';
import { ColumnsType } from 'antd/lib/table';
import { CheckSquareFilled, DeleteOutlined, MinusCircleOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import UserAutocomplete from 'src/common/components/business/user-autocomplete';
import RolesSelect from 'src/common/components/business/roles-select';
import { PermissionsConstants, Selector } from 'src/common/types';
import { UserItemStore, UserSummaryStore } from 'src/stores/users-store';
import { Query } from '../../../core/stores/data-store';
import { RuleObject } from 'rc-field-form/lib/interface';
import LicenseAttributesComponent from '../../licenses/license-attributes/license-attributes';
import { LicenseCreationAttributesStore, LicenseManagerAssignmentAttributes, NewLicensCreationAttributes } from '../../../stores/licensing-store';
import { GlobalLicenseManagerRoleName, LicenseApproverRoleName, LicenseManagerRoleName, StructmasterUserRoleName } from '../../../stores/roles-store';
import OrganizationMemberRoleEdit from './organization-member-role-edit';
import { startCase } from 'lodash';
import { ENV } from 'src/';
import { useCurrentUserTenantPermissionsFilter } from 'src/common/permission-hooks';
import { useIdentity } from 'src/core/services/authentication';
import { useNavigate } from 'react-router-dom';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { RoleAssignmentAttributes } from 'src/stores/role-assignments-store';
import RoleAssignmentAttributesComponent from 'src/pages/role-assignments/role-attributes/role-attributes';
import { isEmailOrGuid } from 'src/core/utils/object';
import { curateString } from 'src/core/utils/object';
const { Text } = Typography;

const AddOrganizationMembersModal: FC<{
  organizationId: string;
  organizationName: string;
  organizationUserLimit: number;
  organizationUserCount: number;
  onAddMembers: (users: OrganizationMemberItem[]) => any;
  onClose: () => void;
  exclude?: string[];
  maxSelector?: Selector;
}> = (props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [customError, setCustomError] = useState('');
  const usersStore = useMemo(() => container.get(UserSummaryStore), []);
  const usersState = usersStore.state;
  const licenseCreationAttributesStore = container.get(LicenseCreationAttributesStore);
  const [licenseAttributesVisible, setLicenseAttributesVisible] = useState<{ [key: number]: boolean }>({});
  const [licenseAttributes, setLicenseAttributes] = useState<{ [key: number]: LicenseManagerAssignmentAttributes }>();
  const [roleAttributes, setRoleAttributes] = useState<{ [key: number]: RoleAssignmentAttributes }>();

  useEffect(() => {
    var query = {
      take: 10000,
      filter: [{ Organizations: { any: { OrganizationId: { value: props.organizationId, type: 'guid' } } } }],
    } as Query;
    usersStore.load(query);
  }, [props.organizationId]); // eslint-disable-line react-hooks/exhaustive-deps
  const onAddRow = () => {
    var attributes = { ...licenseAttributesVisible };
    attributes[getGreaterKey() + 1] = false;
    setLicenseAttributesVisible(attributes);
  };
  const onRemoveRow = (index: number) => {
    var attributes = { ...licenseAttributesVisible };
    delete attributes[index];
    setLicenseAttributesVisible(attributes);
  };
  const onAdd = async () => {
    let item: any;
    try {
      item = await form.validateFields();
    } catch {
      setCustomError(t('PleaseCheckTheInfoProvided'));
      return;
    }
    setCustomError('');
    const users = (item as any).users as { identity: { value: string; label: string }; roles: { value: string; label: string }[] }[];
    (item as any).users = undefined;

    let membersWithRoles: any[] = [];
    for (let i = 0; i < users.length; i++) {
      var user = users[i];
      membersWithRoles.push({
        userId: user.identity.value,
        displayName: user.identity.label,
        email: '',
        roles: (user.roles || []).map((r) =>
          roleAttributes && roleAttributes[i].expirationDate
            ? { role: r.label, roleId: r.value, expirationDate: roleAttributes[i].expirationDate }
            : { role: r.label, roleId: r.value, expirationDate: undefined }
        ),
        resendInvitation: true,
      });
    }

    props.onAddMembers(membersWithRoles).then((success: boolean) => {
      if (success) {
        createLicenseAttributes(membersWithRoles).then(() => {
          form.resetFields();
          // onClose(response);
        });
      }
    });
    props.onClose();
  };
  const createLicenseAttributes = async (users: any[]) => {
    if (licenseAttributes) {
      var items: NewLicensCreationAttributes[] = Object.entries(licenseAttributes).map((x) => {
        return {
          maximumLicenseDueDate: x[1].maximumLicenseDueDate,
          maxLicensesPeriodInDays: x[1].maxLicensesPeriodInDays,
          maxNumberOfLicensesPerMonth: x[1].maxNumberOfLicensesPerMonth,
          allowedDevices: x[1].allowedDevices,
          identityId: users[+x[0]].userId,
          scopeId: props.organizationId,
          canCreateUnlimitedLicense: x[1].canCreateUnlimitedLicense,
          needsSignOff: x[1].needsSignOff,
          canCreateRecurrentLicenses: x[1].canCreateRecurrentLicenses ?? false,
        } as NewLicensCreationAttributes;
      });

      await licenseCreationAttributesStore.createMany(items);
    }
  };
  const onRolesChange = (values: { value: string; label: string; selector: Selector }[], index: number | undefined) => {
    if (index !== undefined) {
      var licenseMnagerRoleSelected = values.any((x) => x.label === LicenseManagerRoleName);
      if (!licenseAttributesVisible[index] && licenseMnagerRoleSelected) {
        let attributesVisible = { ...licenseAttributesVisible };
        attributesVisible[index] = true;
        setLicenseAttributesVisible(attributesVisible);
      } else if (!licenseMnagerRoleSelected && licenseAttributesVisible[index]) {
        let attributesVisible = { ...licenseAttributesVisible };
        attributesVisible[index] = false;
        setLicenseAttributesVisible(attributesVisible);
      }
    }
  };

  const duplicatedUserEntry = async (userId: string) => {
    try {
      var item = await form.getFieldsValue();
      var users = item.users.filter((x: any) => x.identity.value === userId);
      return users && users.length > 1;
    } catch {
      setCustomError(t('PleaseCheckTheInfoProvided'));
    }
    return false;
  };

  const validator = async (ruleObj: RuleObject, value: any): Promise<void> => {
    if (value?.value == null) return Promise.reject(t('SelectOrInviteWarning'));
    else if (!isEmailOrGuid(value.value)) return Promise.reject(t('InvalidEmailWarning'));
    else if (usersState.value.items.find((x) => x.id === value.value)) {
      return Promise.reject(t('MemberAlreadyInOrganizarion').replace('{0}', props.organizationName));
    } else if (await duplicatedUserEntry(value.value)) {
      return Promise.reject(t('YouHaveSelectedThisUserMoreThanOnce'));
    } else return Promise.resolve();
  };
  const getGreaterKey = (): number => {
    const keys = Object.keys(licenseAttributesVisible).map(Number);
    return Math.max(...keys, -1);
  };
  const onLicenseAttributesChange = (item: LicenseManagerAssignmentAttributes, fieldIndx: number) => {
    var attr = { ...licenseAttributes };
    attr[fieldIndx] = item;
    setLicenseAttributes(attr);
  };
  const onRoleAttributesChange = (item: RoleAssignmentAttributes, fieldIdx: number) => {
    var attr = { ...roleAttributes };
    attr[fieldIdx] = item;
    setRoleAttributes(attr);
  };
  return (
    <Modal
      title={<h3 className="figma-modal__title">{t('AddMembersToOrganization')}</h3>}
      open={true}
      onOk={onAdd}
      onCancel={props.onClose}
      okButtonProps={{ style: { width: '45%', float: 'right' } }}
      cancelButtonProps={{ type: 'default', style: { width: '45%' } }}
      okText={t('AddMembers')}
      cancelText={t('Cancel')}
      width={560}
    >
      {(customError || '').length !== 0 && <Alert type="error" message={customError} closable onClose={() => setCustomError('')} />}
      {usersState && usersState.value.items && (
        <Form form={form} layout="vertical" autoComplete="off">
          <Alert
            type="warning"
            message={t('OrganizationUserLimitWarningParam')
              .replace('{0}', `${props.organizationUserLimit}`)
              .replace('{1}', `${props.organizationUserCount}`)
              .replace('{2}', `${props.organizationUserLimit - props.organizationUserCount}`)}
          />
          <Form.List name="users">
            {(fields, { add, remove }) => (
              <>
                <div style={{ maxHeight: '40vh', overflowY: 'auto', overflowX: 'hidden' }}>
                  {fields.map(({ key, name: fieldIndx, fieldKey, ...restField }) => (
                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Row gutter={[24, 0]}>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            name={[fieldIndx, 'identity']}
                            //fieldKey={[fieldKey, 'identity']}
                            rules={[
                              () => ({
                                required: true,
                                validator,
                              }),
                            ]}
                          >
                            <UserAutocomplete />
                          </Form.Item>
                        </Col>
                        <Col span={10}>
                          <Form.Item
                            {...restField}
                            name={[fieldIndx, 'roles']}
                            //fieldKey={[fieldKey, 'roles']}
                            rules={[{ required: true, message: t('PleaseSelectOrRemoveRow') }]}
                          >
                            <RolesSelect
                              excludeNames={[!(props.organizationName.toLowerCase() === 'sptab') ? GlobalLicenseManagerRoleName : '']}
                              onChange={onRolesChange}
                              organizationId={props.organizationId}
                              maxSelector={props.maxSelector}
                              index={fieldIndx}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={2}>
                          <MinusCircleOutlined
                            onClick={() => {
                              remove(fieldIndx);
                              onRemoveRow(fieldIndx);
                            }}
                          />
                        </Col>
                        <Col span={24}>
                          <Text style={{ marginBottom: 10 }} strong>
                            {t('RoleAssignmentAttributes')}:
                          </Text>
                          <RoleAssignmentAttributesComponent
                            customFormFieldName={[fieldIndx, 'roles-expiration']}
                            onChange={(item) => onRoleAttributesChange(item, fieldIndx)}
                          />
                          {licenseAttributesVisible[fieldIndx] && (
                            <>
                              <Divider />
                              <Text style={{ marginBottom: 10 }} strong>
                                {t('LicenseManagerRoleAttributes')}:
                              </Text>
                              <LicenseAttributesComponent
                                onChange={(item: LicenseManagerAssignmentAttributes) => onLicenseAttributesChange(item, fieldIndx)}
                              />
                            </>
                          )}
                        </Col>
                      </Row>
                    </Space>
                  ))}
                </div>
                <Form.Item>
                  <Button
                    type="dashed"
                    disabled={fields.length >= props.organizationUserLimit - props.organizationUserCount}
                    onClick={() => {
                      add();
                      onAddRow();
                    }}
                    block
                    icon={<PlusOutlined />}
                  >
                    {t('Add')}
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      )}
    </Modal>
  );
};

const permissions = [PermissionsConstants.ManageUsers, PermissionsConstants.ManageGlobalLicenses, PermissionsConstants.ManageLicenses];

const OrganizationMembersTab: FC<{ canManageUsers: boolean; canAssignRoles: boolean }> = ({ canAssignRoles, canManageUsers, ...props }) => {
  const identity = useIdentity();
  const { t } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);
  const organizationStore = container.get(OrganizationItemStore);
  const userStore = container.get(UserItemStore);
  const organizationState = organizationStore.state;
  const [error, setError] = useState('');
  const [addMembersVisible, setAddMembersVisible] = useState(false);
  const [editMemberRolesVisible, setEditMemberRolesVisible] = useState(false);
  const [selectedUserRoles, setSelectedUserRoles] = useState<{ label: string; value: string; expirationDate?: Date }[]>();
  const selectedUserId = useRef<OrganizationMemberItem>();
  const navigate = useNavigate();
  const [userPermissionsSet, checkingPermissions] = useCurrentUserTenantPermissionsFilter(identity, permissions);
  const hasGlobalLicenseManagerPermission = userPermissionsSet.has(PermissionsConstants.ManageGlobalLicenses);
  const hasLicenseManagerPermission = !checkingPermissions && userPermissionsSet.has(PermissionsConstants.ManageLicenses);
  const { modal } = App.useApp();

  const [selectedRowKeys, setSelectedRowKeys] = useState([] as any[]);

  const searchInputRef = useRef<InputRef>(null);

  const handleSearch = (_: any, confirm: () => void) => {
    confirm();
  };

  const handleSearchReset = (clearFilters: (() => void) | undefined, confirm: (() => void) | undefined) => {
    clearFilters && clearFilters();
    confirm && confirm();
  };

  const onReload = async () => {
    await organizationStore.load(organizationState.item.value.id);
  };

  const onAddOrganizationMembers = async (newMembers: OrganizationMemberItem[]) => {
    const response = await organizationStore.addOrganizationMembers(organizationState.item.value.id, newMembers);
    if (response && response?.isSuccess) {
      setError('');
      organizationStore.clearMessages();
      await onReload();
    } else {
      setError(config.environment === ENV.DEV ? organizationState.errorMessage.value || t('UnexpectedError') : t('ErrorAddingOrganizationMembers'));
    }
    return response?.isSuccess;
  };

  const onDeleteOrganizationMembers = () => {
    const organizationIds = [...selectedRowKeys];
    modal.confirm({
      title: <h3>{`${t('Confirmation')}`}</h3>,
      icon: null,
      onOk: async () => {
        const response = await organizationStore.deleteOrganizationMembers(organizationState.item.value.id, organizationIds);
        if (response && response?.isSuccess) {
          setSelectedRowKeys([]);
          organizationState.set((s) => {
            s.item.members = s.item.members.filter((m) => !organizationIds.includes(m.userId));
            return s;
          });
          setError('');
          organizationStore.clearMessages();
        } else {
          setError(
            config.environment === ENV.DEV ? organizationState.errorMessage.value || t('UnexpectedError') : t('ErrorRemovingOrganizationMembers')
          );
        }
      },
      content:
        organizationIds.length === 1
          ? t('AreYouSureRemoveMemberFromOrg').replace('{0}', '1')
          : t('AreYouSureRemoveMembersFromOrg').replace('{0}', organizationIds.length.toString()),
      okText: t('DeleteUsers'),
      okButtonProps: { style: { width: 120 } },
      cancelText: t('Cancel'),
      cancelButtonProps: { type: 'default', style: { width: 120 } },
    });
  };

  const onSendInvitation = async (userId: string) => {
    const response = await organizationStore.sendInvitation(organizationState.item.value.id, userId);

    if (response && response?.isSuccess) {
      modal.success({
        title: <h3>{`${t('InvitationSent')}`}</h3>,
      });
    } else {
      modal.error({
        title: <h3>{`${t('InvitationNotSent')}`}</h3>,
        content: config.environment === ENV.DEV ? response?.error || t('UnexpectedError') : t('InvitationNotSentErrorMessage'),
      });
    }
  };

  const onSendResetPasswordEmail = async (userId: string) => {
    const response = await userStore.sendResetPasswordEmail(userId);

    if (response && response?.isSuccess) {
      modal.success({
        title: <h3>{`${t('ResetPasswordEmailSent')}`}</h3>,
      });
    } else {
      modal.error({
        title: <h3>{`${t('ResetPasswordEmailNotSent')}`}</h3>,
        content: config.environment === ENV.DEV ? response?.error || t('UnexpectedError') : t('ResetPasswordEmailNotSentMessage'),
      });
    }
  };

  const columns: ColumnsType<OrganizationMemberItem> = [
    {
      title: t('User'),
      dataIndex: 'displayName',
      sorter: (a, b) => a.displayName.localeCompare(b.displayName),
      defaultSortOrder: 'ascend',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            ref={searchInputRef}
            placeholder={t('Search')}
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => handleSearch(selectedKeys, confirm)}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              disabled={((selectedKeys[0] ?? '') as string).trim().length === 0}
              onClick={() => handleSearch(selectedKeys, confirm)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              {t('Search')}
            </Button>
            <Button onClick={() => handleSearchReset(clearFilters, confirm)} size="small" style={{ width: 90 }}>
              {t('Reset')}
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => curateString(record.displayName.toString().toLowerCase()).includes(curateString(value.toString().toLowerCase())),
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInputRef.current?.select(), 100);
        }
      },
      render: (displayName, record) =>
        userPermissionsSet.has(PermissionsConstants.ManageUsers) ? (
          <span
            className="clickable-span"
            onClick={() =>
              navigate(`/users/${record.userId}`, {
                state: { organizationId: organizationState.item.id.value, organizationDisplayName: organizationState.item.displayName.value },
              })
            }
          >
            {displayName}
          </span>
        ) : (
          <span>{displayName}</span>
        ),
    },
    {
      title: '',
      dataIndex: 'userId',
      render: (userId, record) => (
        <>
          {canManageUsers && (
            <>
              <span className="clickable-span strong" onClick={() => onSendResetPasswordEmail(userId)}>
                {t('SendResetPasswordEmail')}
              </span>

              {record.resendInvitation && <span style={{ color: '#E1E1E1' }}>{' | '}</span>}
              {record.resendInvitation && (
                <span className="clickable-span strong" onClick={() => onSendInvitation(userId)}>
                  {t('ResendInvitation')}
                </span>
              )}
            </>
          )}
        </>
      ),
    },
  ];

  const updateSelectedUserRoles = () => {
    if (selectedUserId.current) {
      setSelectedUserRoles(
        selectedUserId.current.roles.map((x) => {
          const roleName = startCase(x.role.toLowerCase());
          return { label: roleName, value: x.roleId, expirationDate: x.expirationDate };
        })
      );
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selRowKeys: any[], selectedRows: OrganizationMemberItem[]) => {
      setSelectedRowKeys(selRowKeys);
      if (selRowKeys.length === 1) {
        selectedUserId.current = selectedRows[0];
        updateSelectedUserRoles();
      }
    },
  };

  const orgBusy = organizationState.isBusy.value;
  const userBusy = userStore.state.isBusy.value;

  const getLockedRoles = () => {
    const locked = [];
    if (!hasGlobalLicenseManagerPermission) locked.push(LicenseManagerRoleName);
    if (!hasGlobalLicenseManagerPermission) locked.push(LicenseApproverRoleName);
    if (!hasLicenseManagerPermission) locked.push(StructmasterUserRoleName);
    return locked;
  };

  if (organizationState.item.value?.creationStatus !== OrganizationCreationStatus.Available)
    return (
      <div>
        <Row style={{ padding: '0 40px' }}>
          <Col span="12">
            <Row gutter={[8, 0]}>
              <Col>
                <h3 style={{ marginTop: 12 }}>{t('OrganizationMembers')}</h3>
              </Col>
            </Row>
          </Col>
        </Row>
        {error.length !== 0 && <Alert type="error" message={t(error || '')} />}
        <div>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('InitializingOrganization')}></Empty>
        </div>
      </div>
    );

  return (
    <div>
      <Spin spinning={orgBusy || userBusy || checkingPermissions}>
        <Row style={{ padding: '0 40px' }}>
          <Col lg={12} xs={24}>
            <Row gutter={[8, 0]}>
              {selectedRowKeys.length === 0 && (
                <Col>
                  <h3 style={{ marginTop: 12 }}>{t('OrganizationMembers')}</h3>
                </Col>
              )}
              {selectedRowKeys.length !== 0 && (
                <h3 style={{ marginTop: 12 }}>
                  {selectedRowKeys.length} {t('RowsSelected')}
                </h3>
              )}
              {selectedRowKeys.length !== 0 && (
                <CheckSquareFilled style={{ marginLeft: 15, marginBottom: 10, marginTop: 12, fontSize: 18, color: '#AB162B' }} />
              )}
            </Row>
          </Col>
          <Col lg={12} xs={24}>
            <Row justify="end">
              {selectedRowKeys.length === 1 && canAssignRoles && (
                <Tooltip placement="top" title={t('EditRoles')}>
                  <Button type="primary" onClick={() => setEditMemberRolesVisible(true)} style={{ marginBottom: 16, marginRight: 10, width: 100 }}>
                    {t('EditRoles')}
                  </Button>
                </Tooltip>
              )}
              {selectedRowKeys.length !== 0 && canManageUsers && (
                <Col>
                  <Tooltip placement="top" title={selectedRowKeys.length === 1 ? t('RemoveMember') : t('RemoveMembers')}>
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={onDeleteOrganizationMembers}
                      type="primary"
                      style={{ marginBottom: 16, width: 56 }}
                    ></Button>
                  </Tooltip>
                </Col>
              )}
              {selectedRowKeys.length === 0 && (
                <Col>
                  <Tooltip placement="top" title={t('Reload')}>
                    <Button
                      className="ant-btn-secondary"
                      icon={<ReloadOutlined style={{ color: '#AB162B' }} />}
                      onClick={onReload}
                      style={{ marginBottom: 16, marginRight: 10, width: 56 }}
                    ></Button>
                  </Tooltip>
                  {canManageUsers && (
                    <Tooltip placement="top" title={t('AddMembers')}>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setAddMembersVisible(true)}
                        style={{ marginBottom: 16, width: 56 }}
                      ></Button>
                    </Tooltip>
                  )}
                </Col>
              )}
            </Row>
          </Col>
        </Row>

        {error.length !== 0 && <Alert type="error" message={t(error || '')} />}
        <div>
          <Table
            rowKey="userId"
            rowSelection={canManageUsers || canAssignRoles ? rowSelection : undefined}
            columns={columns}
            dataSource={(organizationState.item.value as OrganizationItem)?.members ?? []}
          />
        </div>
      </Spin>
      {addMembersVisible && (
        <AddOrganizationMembersModal
          organizationUserCount={organizationState.item.value.members.length}
          organizationUserLimit={ORGANIZATIONS_MEMBER_LIMIT}
          organizationId={organizationState.item.id.value}
          organizationName={organizationState.item.displayName.value}
          exclude={(organizationState.item.value?.members ?? []).map((r) => r.userId)}
          onAddMembers={onAddOrganizationMembers}
          onClose={() => setAddMembersVisible(false)}
          maxSelector={organizationState.item.value.isPartner ? Selector.Partners : Selector.Organization}
          {...props}
        />
      )}
      {editMemberRolesVisible && (
        <OrganizationMemberRoleEdit
          excludeFromDeleteRoleNames={[StructmasterUserRoleName]}
          lockedRoleNames={getLockedRoles()}
          isSptAb={organizationState.item.displayName.value.toLowerCase() === 'sptab'}
          isPartner={organizationState.item.isPartner.value}
          organizationId={organizationState.item.id.value}
          identityId={selectedRowKeys[0]}
          selectedRoles={selectedUserRoles!}
          onClose={async (changed: boolean) => {
            if (changed) {
              await onReload();
              updateSelectedUserRoles();
            }
            setEditMemberRolesVisible(false);
          }}
        />
      )}
    </div>
  );
};

export default OrganizationMembersTab;
